import { useTranslation } from "react-i18next";
import type { ContentFilterCategoryCount, ContentFilterSeverityCount, ContentFilterTotals } from "@/api";
import { BarRow, PageSection, StatRow } from "@/components/section/SectionBlocks";
import { formatNumber } from "@/i18n/format";
import { useEventLabels } from "./contentSafety";

function Bars({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="flex flex-col gap-4">{children}</div>;
}

export function TotalsRow({ totals }: Readonly<{ totals: ContentFilterTotals }>) {
  const { t } = useTranslation("adminContentSafety");
  return (
    <StatRow
      stats={[
        { label: t("stats.flaggedCalls"), value: formatNumber(totals.events ?? 0) },
        { label: t("stats.categoryHits"), value: formatNumber(totals.categoryHits ?? 0) },
        { label: t("stats.blocked"), value: formatNumber(totals.blocked ?? 0) },
        {
          label: t("stats.sources"),
          value: t("stats.sourcesValue", {
            prompt: formatNumber(totals.promptHits ?? 0),
            completion: formatNumber(totals.completionHits ?? 0),
          }),
        },
      ]}
    />
  );
}

export function CategoryBreakdown({ categories }: Readonly<{ categories: ContentFilterCategoryCount[] }>) {
  const { t } = useTranslation("adminContentSafety");
  const labels = useEventLabels();
  const max = Math.max(1, ...categories.map((category) => category.hits));
  return (
    <PageSection title={t("byCategory.title")}>
      <Bars>
        {categories.map((category) => (
          <BarRow
            key={category.category}
            label={labels.category(category.category)}
            value={t("byCategory.value", {
              count: category.hits,
              hits: formatNumber(category.hits),
              blocked: formatNumber(category.blocked),
            })}
            fraction={category.hits / max}
          />
        ))}
      </Bars>
    </PageSection>
  );
}

export function SeverityBreakdown({ severities }: Readonly<{ severities: ContentFilterSeverityCount[] }>) {
  const { t } = useTranslation("adminContentSafety");
  const labels = useEventLabels();
  const max = Math.max(1, ...severities.map((severity) => severity.hits));
  return (
    <PageSection title={t("bySeverity.title")} description={t("bySeverity.description")}>
      <Bars>
        {severities.map((severity) => (
          <BarRow
            key={severity.severity ?? "none"}
            label={labels.severity(severity.severity)}
            value={t("bySeverity.value", { count: severity.hits, hits: formatNumber(severity.hits) })}
            fraction={severity.hits / max}
            tone="secondary"
          />
        ))}
      </Bars>
    </PageSection>
  );
}
